import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  MapPin,
  Phone,
  ShoppingBag,
  UserIcon,
} from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  toast,
  useDebounceCallback,
} from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import {
  useCodRiskSettingsQuery,
  useCustomerQuery,
  useCustomerRiskQuery,
} from "@/modules/customers/presentation/api/queries";
import { CustomerAddressFields } from "@/modules/customers/presentation/ui/CustomerAddressFields";
import { CodOverrideDialog } from "@/modules/customers/presentation/ui/CodOverrideDialog";
import { CustomerCodRiskBanner } from "@/modules/customers/presentation/ui/CustomerCodRiskBanner";
import {
  appendCodOverrideNote,
  needsCodSoftConfirm,
  recommendedPaymentType,
  scoreDraftCustomerRisk,
  toScoreThresholds,
} from "@/modules/customers/presentation/ui/customer-risk";
import { cn } from "@/lib/utils";
import {
  customerAddressFormToPayload,
  EMPTY_CUSTOMER_ADDRESS_FORM,
  needsManualAddressSplit,
  toCustomerAddressFormValue,
  type CustomerAddressFormValue,
} from "@/modules/customers/domain/address";
import { inventoryApi } from "@/modules/inventory/presentation/api/client";
import {
  useRestockWaitlistQuery,
} from "@/modules/restock-waitlist/presentation/api/queries";
import {
  calculateOrderTotals,
  orderLineKey,
} from "@/modules/orders/domain/order-totals";
import type {
  DeliveryMethod,
  PaymentType,
} from "@/modules/orders/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateOrder,
  useCustomerSearch,
  useMarkOrderPaid,
  useOrderQuery,
  useUpdateOrder,
} from "../api/queries";
import {
  buildFifoLotPicks,
  toApiLotPicks,
  type OrderLineLotPick,
} from "../lib/order-lot-allocation";
import type { OrderLineItem } from "../ui/OrderLineItems";
import { OrderLineItems, OrderLineItemsAddBar } from "../ui/OrderLineItems";
import { AdHocKitDialog } from "../ui/AdHocKitDialog";
import { OrderLotPickerSheet } from "../ui/OrderLotPickerSheet";
import { ShippingBranchField } from "../ui/ShippingBranchField";
import { ProductFormSectionLike } from "../ui/OrderSection";
import {
  ProductPickerModal,
  type PickedVariant,
} from "../ui/ProductPickerModal";
import {
  DELIVERY_METHOD_META,
  PAYMENT_TYPE_META,
  SHIPPING_CARRIERS,
} from "../ui/order-status";

type QuickOrderPageProps = {
  orderId?: string;
};

export function QuickOrderPage({ orderId }: QuickOrderPageProps = {}) {
  const nav = useNavigate();
  const isEditMode = Boolean(orderId);
  const prefilledCustomerId = useRouterState({
    select: (state) => {
      if (isEditMode) return undefined;
      const search = state.location.search as { customerId?: string };
      return typeof search.customerId === "string"
        ? search.customerId
        : undefined;
    },
  });
  const prefilledWaitlistId = useRouterState({
    select: (state) => {
      if (isEditMode) return undefined;
      const search = state.location.search as { waitlistId?: string };
      return typeof search.waitlistId === "string"
        ? search.waitlistId
        : undefined;
    },
  });
  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder(orderId ?? "");
  const markPaidOrder = useMarkOrderPaid();
  const orderQuery = useOrderQuery(orderId ?? "");
  const existingOrder = orderQuery.data;

  const [hydrated, setHydrated] = useState(false);

  const [customerId, setCustomerId] = useState<string | undefined>();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressForm, setAddressForm] = useState<CustomerAddressFormValue>(
    EMPTY_CUSTOMER_ADDRESS_FORM,
  );
  const [legacyAddressHint, setLegacyAddressHint] = useState<string | null>(
    null,
  );
  const [deliveryMethod, setDeliveryMethod] =
    useState<DeliveryMethod>("carrier");
  const [carrier, setCarrier] = useState("");
  const [shippingBranch, setShippingBranch] = useState("");
  const [paymentType, setPaymentType] = useState<PaymentType>("prepaid");
  const [codOverrideReason, setCodOverrideReason] = useState<string | null>(
    null,
  );
  const [codOverrideOpen, setCodOverrideOpen] = useState(false);
  const [shippingFee, setShippingFee] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState<OrderLineItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerBonus, setPickerBonus] = useState(false);
  const [kitDialogOpen, setKitDialogOpen] = useState(false);
  const [lotPicker, setLotPicker] = useState<{
    lineKey: string;
    variantId: string;
    variantName: string;
    productName: string;
    stockUnitLabel: string;
    unitsPerSale: number;
    quantity: number;
    isBonus: boolean;
    initialPicks: OrderLineLotPick[];
  } | null>(null);
  const [bumpedLineKey, setBumpedLineKey] = useState<string | null>(null);
  const bumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [customerQuery, setCustomerQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedCustomer = useDebounceCallback(
    (val: string) => setCustomerQuery(val),
    350,
  );
  const customerResults = useCustomerSearch(customerQuery);
  const selectedCustomerProfile = useCustomerQuery(customerId ?? "");
  const prefillCustomerProfile = useCustomerQuery(prefilledCustomerId ?? "");
  const waitlistQuery = useRestockWaitlistQuery(prefilledWaitlistId ?? "");
  const waitlistCustomerProfile = useCustomerQuery(
    waitlistQuery.data?.customerId ?? "",
  );

  const [customerPrefilled, setCustomerPrefilled] = useState(false);
  const [waitlistPrefilled, setWaitlistPrefilled] = useState(false);
  const shippingPrefillForRef = useRef<string | null>(null);
  const riskPaymentPrefillForRef = useRef<string | null>(null);

  const localReservedByVariant = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of items) {
      if (item.lineType === "ad_hoc_kit") continue;
      map[item.variantId] = (map[item.variantId] ?? 0) + item.quantity;
    }
    return map;
  }, [items]);

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (item.isBonus ? 0 : Number(item.saleUnitPrice) * item.quantity),
        0,
      ),
    [items],
  );
  const fee = Number(shippingFee) || 0;
  const discount = Number(discountAmount) || 0;
  const totals = useMemo(
    () => calculateOrderTotals(subtotal, discount, fee),
    [subtotal, discount, fee],
  );
  const total = totals.total;

  const codRiskSettingsQuery = useCodRiskSettingsQuery();
  const customerRiskQuery = useCustomerRiskQuery(
    customerId,
    total > 0 ? total : null,
  );
  const draftRisk = useMemo(() => {
    if (customerId) return null;
    return scoreDraftCustomerRisk(
      total > 0 ? total : null,
      toScoreThresholds(codRiskSettingsQuery.data),
    );
  }, [customerId, total, codRiskSettingsQuery.data]);
  const riskBanner = customerId ? customerRiskQuery.data : draftRisk;

  function flashBumpedLine(lineKey: string) {
    setBumpedLineKey(lineKey);
    if (bumpTimerRef.current) clearTimeout(bumpTimerRef.current);
    bumpTimerRef.current = setTimeout(() => setBumpedLineKey(null), 420);
  }

  async function refreshFifoForLine(
    variantId: string,
    quantity: number,
    unitsPerSale = 1,
  ) {
    try {
      const res = await inventoryApi.getVariantStockLots(variantId);
      return buildFifoLotPicks(res.data, quantity, unitsPerSale);
    } catch {
      return [];
    }
  }

  function openLotPickerForLine(lineKey: string) {
    const item = items.find((i) => i.lineKey === lineKey);
    if (!item) return;
    setLotPicker({
      lineKey,
      variantId: item.variantId,
      variantName: item.variantName,
      productName: item.productName,
      stockUnitLabel: item.stockUnitLabel,
      unitsPerSale: item.unitsPerSale,
      quantity: item.quantity,
      isBonus: item.isBonus,
      initialPicks: item.lotPicks,
    });
  }

  function applyLotPicks(picks: OrderLineLotPick[]) {
    if (!lotPicker) return;
    flashBumpedLine(lotPicker.lineKey);
    setItems((prev) =>
      prev.map((i) =>
        i.lineKey === lotPicker.lineKey ? { ...i, lotPicks: picks } : i,
      ),
    );
    setLotPicker(null);
  }

  function openPicker(asBonus: boolean) {
    setPickerBonus(asBonus);
    setPickerOpen(true);
  }

  async function handlePick(picked: PickedVariant) {
    const isBonus = pickerBonus;
    const lineKey = orderLineKey(picked.variantId, isBonus);
    const existing = items.find((i) => i.lineKey === lineKey);
    const quantity = (existing?.quantity ?? 0) + 1;
    const lotPicks = await refreshFifoForLine(
      picked.variantId,
      quantity,
      picked.unitsPerSale,
    );

    flashBumpedLine(lineKey);
    setItems((prev) => {
      if (existing) {
        return prev.map((i) =>
          i.lineKey === lineKey ? { ...i, quantity, lotPicks } : i,
        );
      }
      return [
        ...prev,
        {
          lineKey,
          variantId: picked.variantId,
          productName: picked.productName,
          variantName: picked.variantName,
          saleUnitPrice: picked.unitPrice,
          imageKey: picked.imageKey,
          stockQuantity: picked.stockQuantity,
          stockUnitLabel: picked.stockUnitLabel,
          unitsPerSale: picked.unitsPerSale,
          quantity,
          isBonus,
          lotPicks,
        },
      ];
    });
  }

  function changeQty(lineKey: string, delta: number) {
    flashBumpedLine(lineKey);
    setItems((prev) => {
      const updated = prev
        .map((i) =>
          i.lineKey === lineKey
            ? { ...i, quantity: Math.max(0, i.quantity + delta) }
            : i,
        )
        .filter((i) => i.quantity > 0);

      const item = updated.find((i) => i.lineKey === lineKey);
      if (item && item.lineType !== "ad_hoc_kit") {
        void refreshFifoForLine(
          item.variantId,
          item.quantity,
          item.unitsPerSale,
        ).then((lotPicks) => {
          setItems((current) =>
            current.map((i) =>
              i.lineKey === lineKey ? { ...i, lotPicks } : i,
            ),
          );
        });
      }
      return updated;
    });
  }

  function removeItem(lineKey: string) {
    setItems((prev) => prev.filter((i) => i.lineKey !== lineKey));
  }

  function toggleItemBonus(lineKey: string) {
    setItems((prev) => {
      const item = prev.find((i) => i.lineKey === lineKey);
      if (!item) return prev;

      const newBonus = !item.isBonus;
      const newKey = orderLineKey(item.variantId, newBonus);
      const mergeTarget = prev.find((i) => i.lineKey === newKey);

      if (mergeTarget && mergeTarget.lineKey !== lineKey) {
        return prev
          .filter((i) => i.lineKey !== lineKey)
          .map((i) =>
            i.lineKey === newKey
              ? { ...i, quantity: i.quantity + item.quantity }
              : i,
          );
      }

      return prev.map((i) =>
        i.lineKey === lineKey
          ? { ...i, lineKey: newKey, isBonus: newBonus }
          : i,
      );
    });
  }

  /** ซื้อ 10 แถม 1 — เพิ่มแถวແຖມ + FIFO lot อัตโนมัติ */
  async function addBonusForLine(saleLineKey: string) {
    const item = items.find((i) => i.lineKey === saleLineKey);
    if (!item || item.isBonus) return;

    const bonusKey = orderLineKey(item.variantId, true);
    const bonusRow = items.find((i) => i.lineKey === bonusKey);
    const quantity = (bonusRow?.quantity ?? 0) + 1;
    const lotPicks = await refreshFifoForLine(
      item.variantId,
      quantity,
      item.unitsPerSale,
    );

    flashBumpedLine(bonusKey);
    setItems((prev) => {
      if (bonusRow) {
        return prev.map((i) =>
          i.lineKey === bonusKey ? { ...i, quantity, lotPicks } : i,
        );
      }
      return [
        ...prev,
        {
          lineKey: bonusKey,
          variantId: item.variantId,
          productName: item.productName,
          variantName: item.variantName,
          saleUnitPrice: item.saleUnitPrice,
          imageKey: item.imageKey,
          stockQuantity: item.stockQuantity,
          stockUnitLabel: item.stockUnitLabel,
          unitsPerSale: item.unitsPerSale,
          quantity,
          isBonus: true,
          lotPicks,
        },
      ];
    });
  }

  function applyCustomer(c: {
    id: string;
    name: string;
    phone: string | null;
    address?: string | null;
    village?: string | null;
    district?: string | null;
    province?: string | null;
    addressLine?: string | null;
  }) {
    setCustomerId(c.id);
    setName(c.name);
    setPhone(c.phone ?? "");
    setAddressForm(toCustomerAddressFormValue(c));
    setLegacyAddressHint(
      needsManualAddressSplit(c) ? (c.address ?? null) : null,
    );
    setShowSuggestions(false);
    shippingPrefillForRef.current = null;
  }

  function applyLastShipping(shipping: {
    deliveryMethod: DeliveryMethod;
    shippingCarrier: string | null;
    shippingBranch: string | null;
    shippingFee: string;
  }) {
    setDeliveryMethod(shipping.deliveryMethod);
    if (shipping.deliveryMethod === "carrier") {
      setCarrier(shipping.shippingCarrier ?? "");
      setShippingBranch(shipping.shippingBranch ?? "");
      setShippingFee(
        Number(shipping.shippingFee) > 0 ? String(shipping.shippingFee) : "",
      );
      return;
    }
    setCarrier("");
    setShippingBranch("");
    setShippingFee(
      shipping.deliveryMethod === "pickup"
        ? ""
        : Number(shipping.shippingFee) > 0
          ? String(shipping.shippingFee)
          : "",
    );
  }

  const canSubmit = name.trim().length > 0 && items.length > 0;
  const isSubmitting =
    createOrder.isPending || updateOrder.isPending || markPaidOrder.isPending;

  useEffect(() => {
    if (
      isEditMode ||
      customerPrefilled ||
      prefilledWaitlistId ||
      !prefilledCustomerId ||
      !prefillCustomerProfile.data
    ) {
      return;
    }
    applyCustomer(prefillCustomerProfile.data);
    setCustomerPrefilled(true);
  }, [
    isEditMode,
    customerPrefilled,
    prefilledWaitlistId,
    prefilledCustomerId,
    prefillCustomerProfile.data,
  ]);

  useEffect(() => {
    if (isEditMode || waitlistPrefilled || !prefilledWaitlistId) return;
    const entry = waitlistQuery.data;
    if (!entry) return;

    if (entry.status !== "ready" && entry.status !== "notified") {
      toast.error("ລາຍການນີ້ສ້າງອໍເດີ້ບໍ່ໄດ້ໃນສະຖານະນີ້");
      setWaitlistPrefilled(true);
      return;
    }

    if (entry.customerId && waitlistCustomerProfile.isLoading) return;

    let cancelled = false;

    async function prefillFromWaitlist() {
      if (waitlistCustomerProfile.data) {
        applyCustomer(waitlistCustomerProfile.data);
        setCustomerPrefilled(true);
      } else {
        setName(entry.customerName);
        setPhone(entry.customerPhone ?? "");
      }

      const orderLines = await Promise.all(
        entry.lines.map(async (line) => {
          const lotPicks = await refreshFifoForLine(
            line.variantId,
            line.quantityRequested,
            line.variantUnitsPerSale,
          );
          return {
            lineKey: orderLineKey(line.variantId, false),
            lineType: "simple" as const,
            variantId: line.variantId,
            productName: line.productName,
            variantName: line.variantName,
            saleUnitPrice: line.variantUnitPrice,
            imageKey: null,
            stockQuantity: line.stockOnHand,
            stockUnitLabel: "ຊິ້ນ",
            unitsPerSale: line.variantUnitsPerSale,
            quantity: line.quantityRequested,
            isBonus: false,
            lotPicks,
          };
        }),
      );

      if (cancelled) return;

      setItems(orderLines);
      setWaitlistPrefilled(true);
    }

    void prefillFromWaitlist();

    return () => {
      cancelled = true;
    };
  }, [
    isEditMode,
    waitlistPrefilled,
    prefilledWaitlistId,
    waitlistQuery.data,
    waitlistCustomerProfile.data,
    waitlistCustomerProfile.isLoading,
  ]);

  /** เลือกลูกค้าเก่า → เติมวิธีรับ/บริษัทขนส่ง/สาขา/ค่าส่ง จากออเดอร์ล่าสุด */
  useEffect(() => {
    if (isEditMode || !customerId) return;
    const profile = selectedCustomerProfile.data;
    if (!profile || profile.id !== customerId) return;
    if (shippingPrefillForRef.current === customerId) return;

    shippingPrefillForRef.current = customerId;
    if (profile.lastShipping) {
      applyLastShipping(profile.lastShipping);
    }
  }, [isEditMode, customerId, selectedCustomerProfile.data]);

  /** ลูกค้าเสี่ยง → default โอนก่อน (ยังกด COD ผ่าน override ได้) */
  useEffect(() => {
    if (isEditMode) return;
    if (!customerId) {
      riskPaymentPrefillForRef.current = null;
      return;
    }
    if (customerRiskQuery.isLoading || !customerRiskQuery.data) return;
    if (riskPaymentPrefillForRef.current === customerId) return;

    riskPaymentPrefillForRef.current = customerId;
    if (needsCodSoftConfirm(customerRiskQuery.data.tier)) {
      setPaymentType("prepaid");
      setCodOverrideReason(null);
    }
  }, [
    isEditMode,
    customerId,
    customerRiskQuery.isLoading,
    customerRiskQuery.data,
  ]);

  useEffect(() => {
    if (!isEditMode || !existingOrder || hydrated) return;
    if (existingOrder.status !== "draft") {
      toast.error("ອໍເດີ້ນີ້ແກ້ໄຂບໍ່ໄດ້");
      if (orderId) {
        nav({ to: "/app/orders/$id", params: { id: orderId } });
      }
      return;
    }
    setCustomerId(existingOrder.customer.id);
    setName(existingOrder.customer.name);
    setPhone(existingOrder.customer.phone ?? "");
    setAddressForm(toCustomerAddressFormValue(existingOrder.customer));
    setLegacyAddressHint(
      needsManualAddressSplit(existingOrder.customer)
        ? (existingOrder.customer.address ?? null)
        : null,
    );
    setDeliveryMethod(existingOrder.deliveryMethod);
    setCarrier(existingOrder.shippingCarrier ?? "");
    setShippingBranch(existingOrder.shippingBranch ?? "");
    setPaymentType(existingOrder.paymentType);
    setShippingFee(
      Number(existingOrder.shippingFee) > 0
        ? String(existingOrder.shippingFee)
        : "",
    );
    setDiscountAmount(
      Number(existingOrder.discountAmount) > 0
        ? String(existingOrder.discountAmount)
        : "",
    );
    setNote(existingOrder.note ?? "");
    setItems(
      existingOrder.items.map((item) => {
        const isBonus = item.isBonus;
        const isKit = item.lineType === "ad_hoc_kit";
        return {
          lineKey: isKit
            ? `ad_hoc_kit:${item.id}`
            : orderLineKey(item.variantId, isBonus),
          lineType: isKit ? ("ad_hoc_kit" as const) : ("simple" as const),
          kitName: item.kitName,
          kitComponents: item.kitComponents?.map((comp) => ({
            componentVariantId: comp.componentVariantId,
            quantity: comp.quantity,
            productName: comp.productName,
            variantName: comp.variantName,
            stockQuantity: 0,
          })),
          variantId: item.variantId,
          productName: item.productName,
          variantName: item.variantName,
          saleUnitPrice: isBonus
            ? item.catalogUnitPrice
            : item.unitPrice,
          imageKey: null,
          stockQuantity: 0,
          stockUnitLabel: isKit ? "ຊຸດ" : "ຊິ້ນ",
          unitsPerSale: 1,
          quantity: item.quantity,
          isBonus,
          lotPicks:
            item.lotPicks?.map((pick) => ({
              stockLotId: pick.stockLotId,
              quantity: pick.quantity,
              receiptId: pick.receiptId,
              receiptNumber: pick.receiptNumber,
              quantityRemaining: pick.quantityRemaining,
              unitLandedCost: pick.unitLandedCost,
            })) ?? [],
        };
      }),
    );
    setHydrated(true);
  }, [isEditMode, existingOrder, hydrated, nav, orderId]);

  function buildPayload() {
    return {
      customer: {
        id: customerId,
        name: name.trim(),
        phone: phone.trim() || null,
        ...customerAddressFormToPayload(addressForm),
      },
      deliveryMethod,
      shippingCarrier: deliveryMethod === "carrier" ? carrier || null : null,
      shippingBranch:
        deliveryMethod === "carrier" ? shippingBranch.trim() || null : null,
      paymentType,
      shippingFee: fee,
      discountAmount: discount,
      note:
        paymentType === "cod"
          ? appendCodOverrideNote(note, codOverrideReason)
          : appendCodOverrideNote(note, null, { clear: true }),
      items: items.map((i) => {
        if (i.lineType === "ad_hoc_kit") {
          return {
            lineType: "ad_hoc_kit" as const,
            kitName: i.kitName,
            quantity: i.quantity,
            unitPrice: Number(i.saleUnitPrice),
            isBonus: i.isBonus,
            components: (i.kitComponents ?? []).map((comp) => ({
              componentVariantId: comp.componentVariantId,
              quantity: comp.quantity,
            })),
          };
        }
        return {
          variantId: i.variantId,
          quantity: i.quantity,
          isBonus: i.isBonus,
          lotPicks:
            i.lotPicks.length > 0 ? toApiLotPicks(i.lotPicks) : undefined,
        };
      }),
      ...(prefilledWaitlistId ? { waitlistId: prefilledWaitlistId } : {}),
    };
  }

  function handleAddKit(item: OrderLineItem) {
    flashBumpedLine(item.lineKey);
    setItems((prev) => [...prev, item]);
  }

  function handleDeliveryMethodChange(method: DeliveryMethod) {
    setDeliveryMethod(method);
    if (method !== "carrier") {
      setCarrier("");
      setShippingBranch("");
    }
    if (method === "pickup") {
      setShippingFee("");
    }
  }

  function submit(markPaid: boolean) {
    if (!canSubmit) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກค้າ ແລະ ເລືອກสินค้าຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }

    if (isEditMode && orderId) {
      updateOrder.mutate(buildPayload(), {
        onSuccess: () => {
          if (markPaid) {
            markPaidOrder.mutate(orderId, {
              onSuccess: (order) => {
                toast.success(
                  order.paymentType === "cod" ? "ຢືນຢັນ COD ແລ້ວ" : "ຊຳລະແລ້ວ",
                  { description: order.orderNumber },
                );
                nav({ to: "/app/orders/$id", params: { id: order.id } });
              },
              onError: (e) =>
                toast.error("ຊຳລະບໍ່ສຳເລັດ", {
                  description: e instanceof Error ? e.message : undefined,
                }),
            });
          } else {
            toast.success("ບັນທຶກການແກ້ໄຂແລ້ວ", {
              description: existingOrder?.orderNumber,
            });
            nav({ to: "/app/orders/$id", params: { id: orderId } });
          }
        },
        onError: (e) =>
          toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      });
      return;
    }

    createOrder.mutate(
      { ...buildPayload(), markPaid },
      {
        onSuccess: (order) => {
          toast.success(markPaid ? "ສ້າງ ແລະ ຊຳລະແລ້ວ" : "ບັນທຶກຮ່າງແລ້ວ", {
            description: order.orderNumber,
          });
          nav({ to: "/app/orders/$id", params: { id: order.id } });
        },
      },
    );
  }

  if (isEditMode && orderQuery.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6 pb-32">
          <div className="h-10 w-64 animate-pulse rounded-lg bg-muted" />
          <div className="h-40 animate-pulse rounded-xl bg-muted" />
          <div className="h-60 animate-pulse rounded-xl bg-muted" />
        </Main>
      </>
    );
  }

  if (isEditMode && !existingOrder) {
    return (
      <>
        <Header />
        <Main className="flex flex-col items-center gap-4 py-20 text-center">
          <p className="text-muted-foreground">ບໍ່ພົບອໍເດີ້ນີ້</p>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => nav({ to: "/app/orders" })}
          >
            ກັບໄປລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6 pb-32">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              {isEditMode ? "ແກ້ໄຂອໍເດີ້ຮ່າງ" : "ສ້າງອໍເດີ້ດ່ວນ"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isEditMode
                ? existingOrder?.orderNumber
                : "ເລືອກສິນຄ້າ ລະບົບຄິດຍອດໃຫ້ອັດຕະໂນມັດ"}
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="shrink-0 rounded-full border-secondary/40"
            onClick={() =>
              isEditMode && orderId
                ? nav({ to: "/app/orders/$id", params: { id: orderId } })
                : nav({ to: "/app/orders" })
            }
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບຄืນ
          </Button>
        </div>

        <ProductFormSectionLike icon={UserIcon} title="ຂໍ້ມູນລູກຄ້າ">
          {customerId && selectedCustomerProfile.data ? (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {selectedCustomerProfile.data.stats.isReturning ? (
                <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                  ຊື້ຊ້ຳ · {selectedCustomerProfile.data.stats.orderCount} ອໍເດີ້
                </Badge>
              ) : selectedCustomerProfile.data.stats.orderCount > 0 ? (
                <Badge variant="secondary" className="rounded-full">
                  ລູກຄ້າໃໝ່
                </Badge>
              ) : null}
              {selectedCustomerProfile.data.stats.lastOrderAt ? (
                <span className="text-muted-foreground text-xs">
                  ອໍເດີ້ລ່າສຸດ{" "}
                  {new Date(
                    selectedCustomerProfile.data.stats.lastOrderAt,
                  ).toLocaleDateString("lo-LA")}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldLayout className="relative">
              <FieldLabel htmlFor="cust-name">ຊື່ລູກຄ້າ *</FieldLabel>
              <FieldContent>
                <Input
                  id="cust-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setCustomerId(undefined);
                    debouncedCustomer(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => name && setShowSuggestions(true)}
                  placeholder="ຊື່ລູກຄ້າ"
                  autoComplete="off"
                />
              </FieldContent>
            </FieldLayout>

            <FieldLayout>
              <FieldLabel htmlFor="cust-phone">
                <Phone className="size-4" /> ເບີໂທ / WhatsApp
              </FieldLabel>
              <FieldContent>
                <Input
                  id="cust-phone"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCustomerId(undefined);
                    debouncedCustomer(e.target.value);
                    setShowSuggestions(true);
                  }}
                  placeholder="020 xxxx xxxx"
                  inputMode="tel"
                  autoComplete="off"
                />
              </FieldContent>
            </FieldLayout>
          </div>

          {showSuggestions &&
          customerQuery.trim().length >= 2 &&
          !customerId &&
          (customerResults.data?.data?.length ?? 0) > 0 ? (
            <div className="rounded-xl border border-secondary/20 bg-card p-1">
              <p className="px-3 py-1.5 text-muted-foreground text-xs">
                ລูກຄ້າເກົ່າ — ກົດເພື່ອເຕີມຂໍ້ມູນ
              </p>
              {customerResults.data?.data?.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => applyCustomer(c)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/10"
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {c.phone}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <CustomerAddressFields
            value={addressForm}
            onChange={setAddressForm}
            idPrefix="quick-cust"
            legacyAddressHint={legacyAddressHint}
          />

          {riskBanner ? (
            <CustomerCodRiskBanner
              risk={riskBanner}
              prominent
              className="mt-4"
            />
          ) : customerId && customerRiskQuery.isLoading ? (
            <p className="mt-4 text-muted-foreground text-sm">
              ກຳລັງວິເຄາະຄວາມສ່ຽງ COD...
            </p>
          ) : null}
        </ProductFormSectionLike>

        <ProductFormSectionLike
          icon={ShoppingBag}
          title="ລາຍການສິນຄ້າ"
          description={
            items.length > 0
              ? `${items.length} ລາຍການ · ກົດ ± ເພື່ອປັບຈຳນວນ`
              : undefined
          }
          action={
            <OrderLineItemsAddBar
              className="w-full sm:w-auto"
              onAddClick={() => openPicker(false)}
              onAddKitClick={() => setKitDialogOpen(true)}
              onAddBonusClick={() => openPicker(true)}
            />
          }
        >
          <OrderLineItems
            items={items}
            bumpedLineKey={bumpedLineKey}
            onChangeQty={changeQty}
            onRemove={removeItem}
            onToggleBonus={toggleItemBonus}
            onAddBonusForLine={addBonusForLine}
            onEditLots={openLotPickerForLine}
            onAddClick={() => openPicker(false)}
            onAddKitClick={() => setKitDialogOpen(true)}
            onAddBonusClick={() => openPicker(true)}
          />
          {items.length > 0 ? (
            <OrderLineItemsAddBar
              className="border-secondary/15 border-t pt-4 md:hidden"
              onAddClick={() => openPicker(false)}
              onAddKitClick={() => setKitDialogOpen(true)}
              onAddBonusClick={() => openPicker(true)}
            />
          ) : null}
        </ProductFormSectionLike>

        <ProductFormSectionLike icon={MapPin} title="ການຈັດສົ່ງ & ໝາຍເຫດ">
          <FieldLayout>
            <FieldLabel>ວິທີຮັບສິນຄ້າ</FieldLabel>
            <FieldContent>
              <ToggleGroup
                type="single"
                variant="outline"
                value={deliveryMethod}
                onValueChange={(value) => {
                  if (
                    value === "carrier" ||
                    value === "pickup" ||
                    value === "self_delivery"
                  ) {
                    handleDeliveryMethodChange(value);
                  }
                }}
                className="grid w-full grid-cols-3 gap-2"
              >
                {(Object.keys(DELIVERY_METHOD_META) as DeliveryMethod[]).map(
                  (key) => (
                    <ToggleGroupItem
                      key={key}
                      value={key}
                      className="min-h-11 rounded-full px-2 text-xs data-[state=on]:border-secondary data-[state=on]:bg-secondary/15 data-[state=on]:text-foreground sm:text-sm"
                    >
                      {DELIVERY_METHOD_META[key].shortLabel}
                    </ToggleGroupItem>
                  ),
                )}
              </ToggleGroup>
              <p className="mt-1.5 text-muted-foreground text-xs">
                {DELIVERY_METHOD_META[deliveryMethod].hint}
              </p>
            </FieldContent>
          </FieldLayout>

          <div className="grid gap-4 sm:grid-cols-2">
            {deliveryMethod === "carrier" ? (
              <>
                <FieldLayout>
                  <FieldLabel>ບໍລິສັດຂົນສົ່ງ</FieldLabel>
                  <FieldContent>
                    <SimpleSelect
                      value={carrier}
                      onValueChange={(value) => {
                        setCarrier(value);
                        setShippingBranch("");
                      }}
                      placeholder="ເລືອກຂົນສົ່ງ"
                      options={SHIPPING_CARRIERS.map((c) => ({
                        label: c.trim(),
                        value: c,
                      }))}
                    />
                  </FieldContent>
                </FieldLayout>

                <ShippingBranchField
                  id="ship-branch"
                  value={shippingBranch}
                  onChange={setShippingBranch}
                  carrier={carrier}
                  address={addressForm}
                />
              </>
            ) : null}

            <FieldLayout>
              <FieldLabel htmlFor="discount-amount">ສ່ວນລົດ (₭)</FieldLabel>
              <FieldContent>
                <Input
                  id="discount-amount"
                  value={discountAmount}
                  onChange={(e) =>
                    setDiscountAmount(e.target.value.replace(/[^0-9.]/g, ""))
                  }
                  placeholder="0"
                  inputMode="numeric"
                />
              </FieldContent>
            </FieldLayout>

            <FieldLayout>
              <FieldLabel htmlFor="ship-fee">
                ຄ່າຈັດສົ່ງ (₭)
                {deliveryMethod === "pickup" ? (
                  <span className="text-muted-foreground"> — ມັກຈະ 0</span>
                ) : null}
              </FieldLabel>
              <FieldContent>
                <Input
                  id="ship-fee"
                  value={shippingFee}
                  onChange={(e) =>
                    setShippingFee(e.target.value.replace(/[^0-9.]/g, ""))
                  }
                  placeholder="0"
                  inputMode="numeric"
                />
              </FieldContent>
            </FieldLayout>

            <FieldLayout>
              <FieldLabel>ຮູບແບບການຊຳລະ</FieldLabel>
              <FieldContent>
                {(() => {
                  const recommended = recommendedPaymentType(riskBanner?.tier);
                  const riskTone =
                    riskBanner?.tier === "prepaid_only"
                      ? "destructive"
                      : riskBanner?.tier === "require_deposit"
                        ? "warning"
                        : "ok";
                  return (
                    <div className="grid w-full grid-cols-2 gap-2">
                      {(
                        [
                          {
                            value: "prepaid" as const,
                            label: PAYMENT_TYPE_META.prepaid.shortLabel,
                          },
                          {
                            value: "cod" as const,
                            label: PAYMENT_TYPE_META.cod.shortLabel,
                          },
                        ] as const
                      ).map((option) => {
                        const selected = paymentType === option.value;
                        const isRecommended = recommended === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              if (option.value === "prepaid") {
                                setPaymentType("prepaid");
                                setCodOverrideReason(null);
                                return;
                              }
                              if (needsCodSoftConfirm(riskBanner?.tier)) {
                                setCodOverrideOpen(true);
                                return;
                              }
                              setPaymentType("cod");
                            }}
                            className={cn(
                              "relative flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl border px-2 py-2 font-medium text-sm transition-colors",
                              selected
                                ? riskTone === "destructive" &&
                                  option.value === "cod"
                                  ? "border-destructive bg-destructive/10 text-foreground"
                                  : "border-secondary bg-secondary/15 text-foreground"
                                : "border-border bg-background hover:bg-muted/40",
                              isRecommended &&
                                !selected &&
                                "ring-1 ring-secondary/50",
                            )}
                          >
                            {isRecommended ? (
                              <span
                                className={cn(
                                  "absolute -top-2 rounded-full px-2 py-0.5 font-semibold text-[10px]",
                                  riskTone === "destructive"
                                    ? "bg-destructive text-destructive-foreground"
                                    : riskTone === "warning"
                                      ? "bg-secondary text-secondary-foreground"
                                      : "bg-leaf text-white",
                                )}
                              >
                                ແນະນຳ
                              </span>
                            ) : null}
                            <span>{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                <p className="mt-1.5 text-muted-foreground text-xs">
                  {PAYMENT_TYPE_META[paymentType].label}
                </p>
                {riskBanner && needsCodSoftConfirm(riskBanner.tier) ? (
                  <CustomerCodRiskBanner
                    risk={riskBanner}
                    className="mt-2"
                  />
                ) : null}
              </FieldContent>
            </FieldLayout>
          </div>

          <FieldLayout>
            <FieldLabel htmlFor="order-note">ໝາຍເຫດ</FieldLabel>
            <FieldContent>
              <Textarea
                id="order-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ໝາຍເຫດເພີ່ມເຕີມ..."
                rows={2}
              />
            </FieldContent>
          </FieldLayout>
        </ProductFormSectionLike>
      </Main>

      <div className="fixed inset-x-0 bottom-(--mobile-bottom-nav,0px) z-20 border-secondary/20 border-t bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col">
            <span className="text-muted-foreground text-xs">
              ສິນຄ້າ {formatLak(subtotal)}
              {totals.discountAmount > 0
                ? ` − ສ່ວນລົດ ${formatLak(totals.discountAmount)}`
                : ""}
              {fee > 0 ? ` + ສົ່ງ ${formatLak(fee)}` : ""}
            </span>
            {deliveryMethod === "pickup" && fee === 0 ? (
              <span className="text-muted-foreground text-xs">
                ລູກຄ້າມາຮັບທີ່ຮ້ານ
              </span>
            ) : null}
            <span className="font-display text-foreground text-xl">
              ຍອດລວມ: <span className="text-secondary">{formatLak(total)}</span>
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 rounded-full sm:flex-none"
              disabled={!canSubmit}
              isLoading={isSubmitting}
              onClick={() => submit(false)}
            >
              {isEditMode ? "ບັນທຶກການແກ້ໄຂ" : "ບັນທຶກຮ່າງ"}
            </Button>
            <Button
              className="flex-1 rounded-full sm:flex-none"
              disabled={!canSubmit}
              isLoading={isSubmitting}
              onClick={() => submit(true)}
            >
              {paymentType === "cod" ? "ຢືນຢັນ COD" : "ຊຳລະແລ້ວ"}
            </Button>
          </div>
        </div>
      </div>

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePick}
        excludeOrderId={orderId}
        localReservedByVariant={localReservedByVariant}
      />

      <AdHocKitDialog
        open={kitDialogOpen}
        onOpenChange={setKitDialogOpen}
        onConfirm={handleAddKit}
      />

      {lotPicker ? (
        <OrderLotPickerSheet
          open
          onOpenChange={(open) => {
            if (!open) setLotPicker(null);
          }}
          variantId={lotPicker.variantId}
          variantName={lotPicker.variantName}
          productName={lotPicker.productName}
          stockUnitLabel={lotPicker.stockUnitLabel}
          unitsPerSale={lotPicker.unitsPerSale}
          quantity={lotPicker.quantity}
          isBonus={lotPicker.isBonus}
          initialPicks={lotPicker.initialPicks}
          onConfirm={applyLotPicks}
        />
      ) : null}

      {riskBanner && needsCodSoftConfirm(riskBanner.tier) ? (
        <CodOverrideDialog
          open={codOverrideOpen}
          risk={riskBanner}
          onOpenChange={setCodOverrideOpen}
          onConfirm={(reason) => {
            setCodOverrideReason(reason);
            setPaymentType("cod");
            setCodOverrideOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
